import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from '../translations';
import { API_BASE } from '../config';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { KsefUwagiStronaGlowna } from '../components/invoices/KsefUwaga';
import { BladSerweraPocztyStronaGlowna } from '../components/email/BladSerweraPoczty';
import { daysWindow, reservationListQuery } from '../utils/reservationWindow';
import { bezAnulowanych } from '../utils/nadchodzaceRezerwacje';
import { checklistReservationsCount } from '../utils/onboardingChecklist';
import { slideIn, IconBadge, panelTopHairline } from '../ui/common';
import BrandMark from '../components/BrandMark';
import { formatHeaderDate } from '../utils/homeHeader';
import { localeTag } from '../utils/format';
import useToday from '../utils/useToday';
import { AlertIcon } from '../ui/icons';

// Ikony SVG
const StarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" stroke="currentColor" strokeWidth="2"/>
    <polyline points="17,6 23,6 23,12" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const PackageIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" stroke="currentColor" strokeWidth="2"/>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" strokeWidth="2"/>
    <polyline points="3.27,6.96 12,12.01 20.73,6.96" stroke="currentColor" strokeWidth="2"/>
    <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const HomeQuickIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ReservationQuickIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const CalendarQuickIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const ProfileQuickIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="8" r="1" fill="currentColor"/>
  </svg>
);

// helper: format lokalny YYYY-MM-DD (bez konwersji do UTC)
const formatYMDLocal = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 24px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  position: relative;
  min-height: calc(100vh - 48px);
  /* Reszta panelu (18 stron przez PanelContainer, ui/common.js) dostaje przy wejsciu na
     strone delikatny fade+wjazd z dolu. Strona glowna miala WLASNY, niezalezny Container
     bez tej animacji — wejscie na nia bylo „nagie" wsrod stron, ktore juz to maja. */
  animation: ${slideIn} 0.6s ease-out;

  @media (min-width: 1400px) {
    max-width: 1600px;
    padding: 24px 32px;
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
  
  /* Byl tu teczowy pasek z czterech kolorow. Nic nie znaczyl, a jest jednym z
     najczytelniejszych znakow szablonu. Zostaje wlos w kolorze ramki. */
  ${panelTopHairline}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(12px, 2vw, 16px);
  margin-bottom: 32px;
`;

/* Znak the.rENT na currentColor: bierze kolor tekstu, wiec jest widoczny w obu motywach. */
const HeaderMark = styled(BrandMark)`
  flex: none;
  width: clamp(40px, 6vw, 52px);
  height: clamp(40px, 6vw, 52px);
  color: var(--text);
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0 0 2px 0;
  font-family: var(--font-display);
  /* clamp() zamiast stalych 2.25rem: na telefonie 390px staly rozmiar lamal tytul na kilka
     linii i zabieral cwierc ekranu, zanim obsluga zobaczyla cokolwiek uzytecznego. */
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  line-height: 1.15;
  /* Kolor z motywu, bez wypelnienia gradientem: taki tytul nie reaguje na motyw i gubi
     kontrast w trybie ciemnym. */
  color: var(--text);
  letter-spacing: -0.02em;
  overflow-wrap: anywhere;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: clamp(0.9rem, 2.2vw, 1rem);
  color: var(--text-muted);
  font-weight: 400;
`;

const KPICard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px var(--shadow-color);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const KPIHeader = styled.div`
  margin-bottom: 24px;
`;

const KPITitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '';
    width: 3px;
    height: 24px;
    background: var(--primary);
    border-radius: 2px;
  }
`;

const KPISubtitle = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: var(--text-muted);
`;

const KPIStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const KPIStatCard = styled.div`
  background: var(--panel-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--shadow-color);
  }
`;

// X.1: kafelek „Spóźnione zwroty” prowadzi do zakładki „Do zwrotu” w kalendarzu.
const KPIStatLink = styled(KPIStatCard).attrs({ as: Link })`
  text-decoration: none;
  color: inherit;

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

// Wspólna plakietka ikony z ui/common.js (IconBadge) — wołający przekazuje `$tone="steel"` itp.
// zamiast `bg="var(--primary)"`, patrz komentarz przy ICON_TONES w ui/common.js.
const KPIStatIcon = styled(IconBadge).attrs({ $size: 48 })``;

const KPIStatContent = styled.div`
  flex: 1;
`;

const KPIStatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text);
  line-height: 1;
`;

const KPIStatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FavoritesSection = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px var(--shadow-color);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FavoritesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const FavoritesTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '';
    width: 3px;
    height: 24px;
    background: var(--primary);
    border-radius: 2px;
  }
`;

const EditButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.editing ? 'var(--success)' : 'var(--primary)'};
  color: var(--primary-contrast);
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(12px, 1.5vw, 20px);
  margin-bottom: ${props => props.editing ? '16px' : '0'};
  width: 100%;
  
  @media (min-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 1200px) and (min-width: 769px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const DraggableCard = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${props => props.isDragging ? 'var(--primary)' : 'var(--input-border)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  cursor: grab;
  opacity: ${props => props.isDragging ? 0.5 : 1};
  transform: ${props => props.isDragging ? 'scale(1.05)' : 'scale(1)'};
  box-shadow: ${props => props.isDragging ? '0 8px 24px var(--shadow-color)' : '0 2px 8px var(--shadow-color)'};
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:active {
    cursor: grabbing;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || 'var(--primary)'};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px var(--shadow-color);
    
    &::before {
      transform: scaleX(1);
    }
  }

  ${props => props.isOver && !props.isDragging && `
    border-color: var(--success);
    background: color-mix(in srgb, var(--card-bg) 90%, var(--success) 10%);
  `}
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

const SelectableCard = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${props => props.selected ? 'var(--success)' : 'var(--input-border)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px var(--shadow-color);
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || 'var(--primary)'};
    transform: scaleX(${props => props.selected ? 1 : 0});
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px var(--shadow-color);
    border-color: ${props => props.selected ? 'var(--success)' : 'color-mix(in srgb, var(--primary) 30%, transparent)'};
    
    &::before {
      transform: scaleX(1);
    }
  }

  ${props => props.selected && `
    &::after {
      content: '✓';
      position: absolute;
      top: 12px;
      right: 12px;
      width: 28px;
      height: 28px;
      background: var(--success);
      color: var(--primary-contrast);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 16px;
    }
  `}
`;

const DragHandle = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--input-bg);
  border-radius: 8px;
  cursor: grab;
  opacity: 0.6;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    opacity: 1;
    background: var(--primary);
    color: var(--primary-contrast);
  }

  &:active {
    cursor: grabbing;
  }
`;

// PRZENIESIONO: deklaracja RentalListItem musi być przed Dziubek
const RentalListItem = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr 40px;
  gap: 12px;
  padding: 12px;
  background: var(--panel-bg);
  border-radius: 12px;
  margin-bottom: 10px;
  border: 1px solid var(--input-border);
  transition: all 0.2s ease;
  align-items: center;
  
  cursor: pointer;
  &:hover {
    background: var(--table-row-hover);
    transform: translateX(4px);
    box-shadow: 0 4px 12px var(--shadow-color);
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 2fr 1fr 1fr 40px;
    
    /* Ukryj kolumnę z datą końca na mniejszych ekranach */
    > div:nth-child(3) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 10px;
    
    > div {
      display: flex;
      flex-direction: column;
    }
  }
`;

// teraz Dziubek może używać RentalListItem
const Dziubek = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  transition: transform 0.2s ease, color 0.2s ease;
  flex-shrink: 0;

  svg {
    width: 32px;
    height: 32px;
    display: block;
  }

  ${RentalListItem}:hover & {
    transform: translateX(8px) scale(1.1);
    color: var(--primary);
  }
`;

const ActionIcon = styled.div`
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || 'var(--text-muted)'};
  margin-bottom: 12px;
  transition: all 0.3s ease;

  svg {
    width: 30px;
    height: 30px;
    display: block;
  }
  
  ${DraggableCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }

  ${SelectableCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

const ActionTitle = styled.h3`
  margin: 0 0 6px 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text);
`;

const ActionDescription = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-muted);
  line-height: 1.5;
`;

const EditModeInfo = styled.div`
  background: var(--table-row-hover);
  border: 1px solid var(--info-border);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--info);
  font-size: 0.875rem;
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--input-border);
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.disabled ? 'var(--input-bg)' : 'var(--primary)'};
  color: ${props => props.disabled ? 'var(--text-muted)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  font-size: 0.875rem;
  color: var(--text);
  font-weight: 600;
  min-width: 120px;
  text-align: center;
`;

const AllPagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  @media(max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media(max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '';
    width: 3px;
    height: 24px;
    background: var(--primary);
    border-radius: 2px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 16px;
  margin-bottom: 24px;
`;

const Card = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid var(--input-border);
  box-shadow: 0 4px 20px var(--shadow-color);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    /* Wymus stackowanie wewnetrznych divow z inline style display:flex */
    & > div {
      flex-direction: column !important;
      align-items: stretch !important;
      width: 100%;
      gap: 12px;
    }
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text);
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 6px;
  background: var(--input-bg);
  padding: 4px;
  border-radius: 12px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 8px 14px;
  background: ${props => props.active ? 'var(--primary)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--text-muted)'};
  border: none;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.active ? 'white' : 'var(--text)'};
  }
`;

const SegmentedControl = styled.div`
  display: inline-flex;
  background: var(--input-bg);
  padding: 4px;
  border-radius: 12px;
  border: 1px solid var(--input-border);
  gap: 6px;
  align-items: center;
`;

const SegmentButton = styled.button`
  padding: 6px 12px;
  border-radius: 8px;
  border: none;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  background: ${props => props.active ? 'var(--primary)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--text-muted)'};
  transition: all 0.18s ease;

  &:hover {
    color: ${props => props.active ? 'white' : 'var(--text)'};
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    justify-content: flex-start;
  }
`;

const ItemColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemLabel = styled.span`
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const ItemValue = styled.span`
  font-size: 0.9rem;
  color: var(--text);
  font-weight: 600;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: ${props => props.color || 'var(--primary)'};
  color: white;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  width: fit-content;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--text-muted);
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 12px;
    opacity: 0.3;
  }
  
  h4 {
    margin: 0 0 6px 0;
    font-size: 1.125rem;
    color: var(--text);
  }
  
  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

const TopEquipmentGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const TopEquipmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--panel-bg);
  border-radius: 12px;
  border: 1px solid var(--input-border);
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--table-row-hover);
    border-color: color-mix(in srgb, var(--primary) 30%, transparent);
  }
`;

const RankBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  /* zloto/srebro/braz — uniwersalna, powszechnie rozpoznawalna konwencja miejsc na podium,
     celowo niezalezna od motywu panelu (jak medale, nie jak status aplikacji) */
  background: ${props => {
    if (props.rank === 1) return '#F5B301';
    if (props.rank === 2) return '#9AA0A6';
    if (props.rank === 3) return '#B06B2C';
    return 'var(--input-bg)';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.125rem;
  color: ${props => props.rank <= 3 ? 'white' : 'var(--text)'};
  flex-shrink: 0;
`;

const EquipmentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const EquipmentName = styled.div`
  font-weight: 700;
  color: var(--text);
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EquipmentMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 2px;
`;

const RentalCount = styled.div`
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--primary);
  text-align: right;
`;

const CategoryDropdown = styled.div`
  position: relative;
  width: 100%;
  max-width: 280px;
  margin-bottom: 16px;
`;

const CategoryButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: var(--input-bg);
  color: var(--text);
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    border-color: var(--primary);
    background: var(--card-bg);
  }
  
  &[data-open="true"] {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
`;

const CategoryList = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  max-height: 280px;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--shadow-color);
  overflow-y: auto;
  z-index: var(--z-popover);
`;

const CategoryItem = styled.li`
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text);
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: var(--table-row-hover);
  }
  
  &[data-selected="true"] {
    background: rgba(59,130,246,0.14);
    color: var(--primary);
    font-weight: 600;
    
    &::before {
      content: '';
      display: inline-block;
      width: 8px;
      height: 8px;
      margin-right: 10px;
      border-radius: 50%;
      background: var(--primary);
      flex-shrink: 0;
    }
  }
`;

// ZMIEŃ CategoryTabs i CategoryTab na komentarz (nie usuwaj - na wypadek rollbacku)
// const CategoryTabs = styled.div`...`
// const CategoryTab = styled.button`...`

const formatPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';

  if (digits.length === 11 && digits.startsWith('48')) {
    const pl = digits.slice(2);
    return `+48 ${pl.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`;
  }
  if (digits.length === 13 && digits.startsWith('0048')) {
    const pl = digits.slice(4);
    return `+48 ${pl.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`;
  }
  if (digits.length === 9) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
  }
  return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
};

// Wszystkie dostępne strony
// `color` (pasek na górze kafelka) trzyma się TEJ SAMEJ barwy co `iconColor` (ikona) — wcześniej
// miały niezależne, przypadkowo dobrane wartości (np. fioletowa ikona + różowy pasek), więc kafelek
// dostawał dwa niezwiązane ze sobą akcenty. Kolory same w sobie zostają nietokenizowane — to
// dekoracyjne rozróżnienie 7 kafelków dashboardu, nie stany semantyczne (sukces/błąd/...).
const ALL_PAGES = [
  { id: 'home', to: '/', icon: <HomeQuickIcon />, iconColor: '#3b82f6', title: 'Strona główna', description: 'Wróć do ekranu startowego', color: 'var(--primary)' },
  { id: 'reservation', to: '/reservation', icon: <ReservationQuickIcon />, iconColor: '#6366f1', title: 'Nowa rezerwacja', description: 'Stwórz nową rezerwację dla klienta', color: 'var(--primary)' },
  { id: 'calendar', to: '/calendar', icon: <CalendarQuickIcon />, iconColor: '#8b5cf6', title: 'Kalendarz', description: 'Zobacz harmonogram rezerwacji', color: '#8b5cf6' },
  { id: 'clients', to: '/clients', icon: <UsersIcon />, iconColor: '#2563eb', title: 'Klienci', description: 'Zarządzaj bazą klientów', color: '#2563eb' },
  { id: 'warehouse', to: '/inventory', icon: <PackageIcon />, iconColor: '#0ea5e9', title: 'Magazyn', description: 'Przeglądaj i edytuj sprzęt', color: '#0ea5e9' },
  { id: 'statistics', to: '/statistics', icon: <TrendingUpIcon />, iconColor: '#0891b2', title: 'Statystyki', description: 'Analizuj wydajność wypożyczalni', color: '#0891b2' },
  { id: 'profile', to: '/profile', icon: <ProfileQuickIcon />, iconColor: '#16a34a', title: 'Profil', description: 'Zarządzaj ustawieniami konta', color: '#16a34a' }
];

const DEFAULT_FAVORITES = ['reservation', 'calendar', 'statistics', 'profile'];

export default function Home() {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [period, setPeriod] = useState('month');
  const [filteredRentals, setFilteredRentals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [bestSellers, setBestSellers] = useState([]);
  const [isLoadingBest, setIsLoadingBest] = useState(false);
  const [kpis, setKpis] = useState({
    totalRentals: 0,
    availableEquipment: 0,
    activeCustomers: 0,
    overdueReturns: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showOngoing, setShowOngoing] = useState(false);
  // W.6: dane dla checklisty "Pierwsze kroki" — undefined per pole dopóki jego fetch
  // (Promise.allSettled poniżej) się nie rozstrzygnie albo padnie; OnboardingChecklist
  // traktuje undefined jak "nie wiem", nigdy jak "zrobione".
  const [onboardingData, setOnboardingData] = useState({
    equipmentCount: undefined,
    clientsCount: undefined,
    reservationsCount: undefined,
    contractSettings: undefined,
    inventoryConfigured: undefined,
  });
  
  // Stan dla ulubionych
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('homeFavorites');
    return saved ? JSON.parse(saved) : DEFAULT_FAVORITES;
  });
  const [isEditingFavorites, setIsEditingFavorites] = useState(false);
  const [tempFavorites, setTempFavorites] = useState([]);
  
  // Stan dla drag & drop
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Stan dla paginacji rezerwacji
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  // Jedno mapowanie języka na locale dla całej strony (nagłówek i daty rezerwacji), to samo
  // co w utils/format.js: angielski to en-GB (dzień przed miesiącem), niemiecki de-DE.
  const locale = localeTag(language);
  // Dzień z zegarem, nie `new Date()` w renderze: panel zostawiony na noc pokazywał rano
  // wczorajszą datę.
  const today = useToday();

  // Resetuj stronę przy zmianie filtrów
  useEffect(() => {
    setCurrentPage(1);
  }, [period, showOngoing]);

  // Oblicz dane dla bieżącej strony
  const totalPages = Math.ceil(filteredRentals.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentRentals = filteredRentals.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const filterReservationsByPeriod = (reservations, selectedPeriod) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return reservations.filter(rental => {
      const rentalDate = new Date(rental.rawStartDate);
      rentalDate.setHours(0, 0, 0, 0);

      switch (selectedPeriod) {
        case 'day':
          return rentalDate.getTime() === today.getTime();
        case 'week': {
          const endWeek = new Date(today);
          endWeek.setDate(today.getDate() + 7);
          return rentalDate >= today && rentalDate <= endWeek;
        }
        case 'month': {
          const endMonth = new Date(today);
          endMonth.setDate(today.getDate() + 30);
          return rentalDate >= today && rentalDate <= endMonth;
        }
        case 'all':
          return true;
        default:
          return false;
      }
    });
  };

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      try {
        // U.28: bez parametrów serwer oddaje 200 NAJSTARSZYCH rezerwacji, więc przy większej
        // firmie lista „nadchodzących" była pusta. Pytamy o to, co strona pokazuje: trwające dziś
        // i zaczynające się w ciągu 30 dni (najszerszy filtr okresu, `filterReservationsByPeriod`).
        const upcomingQuery = reservationListQuery(daysWindow(new Date(), 30), { limit: 1000 });
        const [resReservations, resKpis, resEquipmentStats, resContractSettings, resInventorySettings] = await Promise.allSettled([
          fetch(`${API_BASE}/api/all-reservations${upcomingQuery}`, { credentials: 'include' }).then(r => {
            if (!r.ok) throw new Error("reservations fetch failed");
            return r.json();
          }),
          fetch(`${API_BASE}/api/kpis`, { credentials: 'include' }).then(r => {
            if (!r.ok) throw new Error("kpis fetch failed");
            return r.json();
          }),
          fetch(`${API_BASE}/api/equipment-stats`, { credentials: 'include' }).then(r => {
            if (!r.ok) throw new Error("equipment-stats fetch failed");
            return r.json();
          }),
          fetch(`${API_BASE}/api/contract-settings`, { credentials: 'include' }).then(r => {
            if (!r.ok) throw new Error("contract-settings fetch failed");
            return r.json();
          }),
          fetch(`${API_BASE}/api/inventory-settings`, { credentials: 'include' }).then(r => {
            if (!r.ok) throw new Error("inventory-settings fetch failed");
            return r.json();
          })
        ]);

        // Anulowane nie należą do „najbliższych wypożyczeń” (przegląd 28.09.2026), reguła w
        // utils/nadchodzaceRezerwacje.js. Lista sprzed odsiania zostaje dla checklisty niżej.
        const reservationsWindow = resReservations.status === 'fulfilled' && Array.isArray(resReservations.value)
          ? resReservations.value
          : [];
        const reservationsData = bezAnulowanych(reservationsWindow);

        const formattedData = reservationsData.map(rental => {
          const startDate = new Date(rental.start_date);
          const endDate = new Date(rental.end_date);
          const days = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

          return {
            id: rental.id,
            name: `${rental.firstname} ${rental.lastname}`,
            equipment: rental.equipment_names,
            days,
            phone: rental.phone || rental.phone_number || '',
            startDate: startDate.toLocaleDateString(locale),
            endDate: endDate.toLocaleDateString(locale),
            rawStartDate: rental.start_date,
            rawEndDate: rental.end_date
          };
        });

        setRentals(formattedData);
        const kpisData = resKpis.status === 'fulfilled' ? resKpis.value : null;
        setKpis({
          totalRentals: kpisData?.totalRentals ?? 0,
          availableEquipment: kpisData?.availableEquipment ?? 0,
          activeCustomers: kpisData?.totalCustomers ?? kpisData?.total_customers ?? 0,
          overdueReturns: kpisData?.overdueReturns ?? 0
        });

        // W.6: liczniki dla checklisty "Pierwsze kroki" — undefined gdy dany fetch padł,
        // żeby OnboardingChecklist mógł odróżnić "nie wiem" od potwierdzonego zera.
        const equipmentStatsData = resEquipmentStats.status === 'fulfilled' ? resEquipmentStats.value : null;
        const contractSettingsData = resContractSettings.status === 'fulfilled' ? resContractSettings.value : null;
        const inventorySettingsData = resInventorySettings.status === 'fulfilled' ? resInventorySettings.value : null;
        setOnboardingData({
          equipmentCount: resEquipmentStats.status === 'fulfilled' ? (equipmentStatsData?.totalEquipment ?? 0) : undefined,
          clientsCount: resKpis.status === 'fulfilled' ? (kpisData?.totalCustomers ?? kpisData?.total_customers ?? 0) : undefined,
          // U.28: lista wyżej obejmuje tylko najbliższe dni, więc krok „pierwsza rezerwacja” liczy
          // się z /api/kpis: `reservationsEver`, czyli wszystkie rezerwacje firmy, także anulowane
          // (`totalRentals` je pomija, to kafelek). Szczegóły i zapasy w checklistReservationsCount.
          reservationsCount: checklistReservationsCount({
            kpisLoaded: resKpis.status === 'fulfilled',
            kpis: kpisData,
            reservations: reservationsWindow,
          }),
          contractSettings: resContractSettings.status === 'fulfilled' ? (contractSettingsData?.settings ?? null) : undefined,
          // E.4: pole `configured` istnieje od E.2 (GET /api/inventory-settings) — żaden nowy endpoint.
          inventoryConfigured: resInventorySettings.status === 'fulfilled' ? (inventorySettingsData?.configured === true) : undefined,
        });
      } catch (e) {
        console.error("Error fetching data:", e);
        setKpis({ totalRentals: 0, availableEquipment: 0, activeCustomers: 0, overdueReturns: 0 });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [locale]);

  useEffect(() => {
    if (!Array.isArray(rentals)) {
      setFilteredRentals([]);
      return;
    }

    if (showOngoing) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const ongoing = rentals.filter(r => {
        const start = new Date(r.rawStartDate);
        const end = new Date(r.rawEndDate ?? r.rawStartDate);
        start.setHours(0,0,0,0);
        end.setHours(0,0,0,0);
        return start.getTime() <= today.getTime() && end.getTime() >= today.getTime();
      });
      setFilteredRentals(ongoing);
    } else {
      setFilteredRentals(filterReservationsByPeriod(rentals, period));
    }
  }, [rentals, period, showOngoing]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/equipment-categories`, {
          credentials: 'include',
          cache: 'no-store'
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        const mapped = (Array.isArray(data) ? data : []).map(r => ({
          key: String(r.id ?? r.name),
          label: r.name,
          value: r.name
        }));
        if (mounted) {
          setCategories(mapped);
          setCategory((prev) => {
            if (!prev) return '';
            const exists = mapped.some((item) => item.value === prev);
            return exists ? prev : '';
          });
        }
      } catch (e) {
        console.error('fetch /api/equipment-categories failed', e);
        if (mounted) {
          setCategories([]);
          setCategory('');
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setIsLoadingBest(true);
      try {
        const url = !category
          ? `${API_BASE}/api/best-sellers-with-counts`
          : `${API_BASE}/api/best-sellers-with-counts?category=${encodeURIComponent(category)}`;
        const res = await fetch(url, { 
          signal: controller.signal,
          credentials: 'include'
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();

        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && typeof data === 'object') {
          const candidates = ['rows','data','items','results','bestSellers','payload'];
          for (const k of candidates) {
            if (Array.isArray(data[k])) { list = data[k]; break; }
          }
          if (!list.length) {
            for (const k of Object.keys(data)) {
              if (Array.isArray(data[k])) { list = data[k]; break; }
            }
          }
        }

        const normalized = (Array.isArray(list) ? list : []).map(item => {
          const rawCount = item?.rentalCount
                        ?? item?.rental_count
                        ?? item?.rentalcount
                        ?? item?.count
                        ?? item?.rentals
                        ?? item?.rental_count_total
                        ?? 0;
          const rentalCount = Number(String(rawCount).replace(/\D/g, '')) || 0;

          return {
            id: item.id ?? item.equipment_id ?? item.item_id ?? null,
            name: item.name ?? item.title ?? item.label ?? item.equipment_name ?? '-',
            brand: item.brand ?? item.manufacturer ?? '',
            size: item.size ?? item.length ?? '',
            category: item.category ?? item.type ?? item.group ?? '',
            rentalCount
          };
        });

        setBestSellers(normalized.slice(0, 5));
      } catch (e) {
        if (e.name !== 'AbortError') console.error('fetch best sellers failed', e);
        setBestSellers([]);
      } finally {
        setIsLoadingBest(false);
      }
    })();
    return () => controller.abort();
  }, [category]);

  const openReservationInCalendar = (rental) => {
    try {
      const date = formatYMDLocal(rental.rawStartDate || rental.startDate);
      navigate(`/calendar?date=${encodeURIComponent(date)}&reservationId=${encodeURIComponent(rental.id)}`);
    } catch (e) {
      console.error('openReservationInCalendar failed', e);
    }
  };

  const handleEditFavorites = () => {
    if (isEditingFavorites) {
      // Zapisz zmiany
      localStorage.setItem('homeFavorites', JSON.stringify(tempFavorites));
      setFavorites(tempFavorites);
      setIsEditingFavorites(false);
    } else {
      // Rozpocznij edycję
      setTempFavorites([...favorites]);
      setIsEditingFavorites(true);
    }
  };

  const handleCancelEdit = () => {
    setTempFavorites([]);
    setIsEditingFavorites(false);
  };

  const toggleFavorite = (pageId) => {
    if (!isEditingFavorites) return;
    
    setTempFavorites(prev => {
      if (prev.includes(pageId)) {
        return prev.filter(id => id !== pageId);
      } else {
        if (prev.length >= 4) {
          return prev;
        }
        return [...prev, pageId];
      }
    });
  };

  const getFavoritePages = () => {
    return favorites.map(fav => ALL_PAGES.find(page => page.id === fav)).filter(Boolean);
  };

  // Funkcje drag & drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newFavorites = [...favorites];
    const draggedItem = newFavorites[draggedIndex];
    newFavorites.splice(draggedIndex, 1);
    newFavorites.splice(dropIndex, 0, draggedItem);

    setFavorites(newFavorites);
    localStorage.setItem('homeFavorites', JSON.stringify(newFavorites));
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // DODAJ nowe stany (zaraz po istniejących useState)
  const [categoryDropdownOpen, setCategoryDropdownOpen] = React.useState(false);
  const categoryDropdownRef = React.useRef(null);

  // DODAJ useEffect dla zamykania dropdowna
  React.useEffect(() => {
    if (!categoryDropdownOpen) return;
    
    const handleClickOutside = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryDropdownOpen]);

  // DODAJ funkcję zmiany kategorii
  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    setCategoryDropdownOpen(false);
  };

  // DODAJ helper dla wybranej kategorii
  const selectedCategoryLabel = React.useMemo(() => {
    if (!categories.length) return 'Brak kategorii';
    const found = categories.find(c => c.value === category);
    return found?.label || 'all';
  }, [categories, category]);

  return (
    <Container>
      <Header data-cy="home-header">
        <HeaderMark data-cy="home-logo" />
        <HeaderText>
          <Title>{t('home.header')}</Title>
          <Subtitle data-cy="home-date">{formatHeaderDate(today, language)}</Subtitle>
        </HeaderText>
      </Header>

      {/* F.19: faktury, które nie trafiły do KSeF (własne zapytanie, best-effort). */}
      <KsefUwagiStronaGlowna />

      {/* Ostatni mail nie wyszedł przez serwer poczty firmy (migracja 065, best-effort). */}
      <BladSerweraPocztyStronaGlowna />

      <OnboardingChecklist
        isLoading={isLoading}
        equipmentCount={onboardingData.equipmentCount}
        clientsCount={onboardingData.clientsCount}
        reservationsCount={onboardingData.reservationsCount}
        contractSettings={onboardingData.contractSettings}
        inventoryConfigured={onboardingData.inventoryConfigured}
      />

      <KPICard>
        <KPIHeader>
          <KPITitle>{t('home.kpisTitle') || 'Kluczowe wskaźniki'}</KPITitle>
          <KPISubtitle>{t('home.kpisSubtitle') || 'Przegląd najważniejszych danych z Twojej wypożyczalni'}</KPISubtitle>
        </KPIHeader>
        
        <KPIStatsGrid>
          <KPIStatCard>
            <KPIStatIcon $tone="dusk">
              <TrendingUpIcon />
            </KPIStatIcon>
            <KPIStatContent>
              <KPIStatValue>{kpis.totalRentals}</KPIStatValue>
              <KPIStatLabel>{t('home.kpis.totalRentals')}</KPIStatLabel>
            </KPIStatContent>
          </KPIStatCard>
          
          <KPIStatCard>
            <KPIStatIcon $tone="pine">
              <PackageIcon />
            </KPIStatIcon>
            <KPIStatContent>
              <KPIStatValue>{kpis.availableEquipment}</KPIStatValue>
              <KPIStatLabel>{t('home.kpis.availableEquipment')}</KPIStatLabel>
            </KPIStatContent>
          </KPIStatCard>
          
          <KPIStatCard>
            <KPIStatIcon $tone="steel">
              <UsersIcon />
            </KPIStatIcon>
            <KPIStatContent>
              <KPIStatValue>{kpis.activeCustomers}</KPIStatValue>
              <KPIStatLabel>{t('home.kpis.clients') || 'Klienci'}</KPIStatLabel>
            </KPIStatContent>
          </KPIStatCard>

          <KPIStatLink to="/calendar?tab=returns" data-cy="home-kpi-overdue">
            <KPIStatIcon $tone={kpis.overdueReturns > 0 ? 'warning' : 'steel'}>
              <AlertIcon />
            </KPIStatIcon>
            <KPIStatContent>
              <KPIStatValue data-cy="home-kpi-overdue-value">{kpis.overdueReturns}</KPIStatValue>
              <KPIStatLabel>{t('home.kpis.overdueReturns')}</KPIStatLabel>
            </KPIStatContent>
          </KPIStatLink>
        </KPIStatsGrid>
      </KPICard>

      <FavoritesSection data-tour="home-favorites">
        <FavoritesHeader>
          <FavoritesTitle>Ulubione</FavoritesTitle>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isEditingFavorites && (
              <EditButton onClick={handleCancelEdit}>
                Anuluj
              </EditButton>
            )}
            <EditButton editing={isEditingFavorites} onClick={handleEditFavorites}>
              {isEditingFavorites ? (
                <>
                  <CheckIcon />
                  Zapisz
                </>
              ) : (
                <>
                  <EditIcon />
                  Edytuj
                </>
              )}
            </EditButton>
          </div>
        </FavoritesHeader>

        {isEditingFavorites && (
          <EditModeInfo>
            <span style={{ display: 'inline-flex', color: 'var(--info)' }}><InfoIcon /></span>
            <div>
              <strong>Tryb edycji:</strong> Kliknij na kafelki poniżej, aby wybrać maksymalnie 4 ulubione strony. 
              Wybrano: {tempFavorites.length}/4
            </div>
          </EditModeInfo>
        )}

        {isEditingFavorites ? (
          <AllPagesGrid>
            {ALL_PAGES.map(page => (
              <SelectableCard
                key={page.id}
                selected={tempFavorites.includes(page.id)}
                color={page.color}
                onClick={() => toggleFavorite(page.id)}
              >
                <ActionIcon color={page.iconColor}>{page.icon}</ActionIcon>
                <ActionTitle>{page.title}</ActionTitle>
                <ActionDescription>{page.description}</ActionDescription>
              </SelectableCard>
            ))}
          </AllPagesGrid>
        ) : (
          <QuickActionsGrid>
            {getFavoritePages().map((page, index) => (
              <DraggableCard
                key={page.id}
                color={page.color}
                draggable={true}
                isDragging={draggedIndex === index}
                isOver={dragOverIndex === index}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <DragHandle title="Przeciągnij, aby zmienić kolejność">
                  ⋮⋮
                </DragHandle>
                <CardContent onClick={() => navigate(page.to)}>
                  <ActionIcon color={page.iconColor}>{page.icon}</ActionIcon>
                  <ActionTitle>{page.title}</ActionTitle>
                  <ActionDescription>{page.description}</ActionDescription>
                </CardContent>
              </DraggableCard>
            ))}
          </QuickActionsGrid>
        )}
      </FavoritesSection>

      <ContentGrid>
        <Card>
          <CardHeader data-tour="home-upcoming">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <SectionTitle style={{ margin: 0 }}>
                {showOngoing
                  ? (t('home.ongoingReservations') || 'Trwające rezerwacje')
                  : (t('home.upcomingReservations') || 'Nadchodzące rezerwacje')}
              </SectionTitle>
              
              <ToggleWrapper>
                <SegmentedControl role="tablist" aria-label="Widok rezerwacji">
                  <SegmentButton
                    role="tab"
                    aria-selected={!showOngoing}
                    active={!showOngoing}
                    onClick={() => setShowOngoing(false)}
                    title={t('home.upcomingReservations') || 'Nadchodzące'}
                  >
                    {t('home.upcomingShort') || 'Nadchodzące'}
                  </SegmentButton>

                  <SegmentButton
                    role="tab"
                    aria-selected={showOngoing}
                    active={showOngoing}
                    onClick={() => setShowOngoing(true)}
                    title={t('home.ongoingReservations') || 'Trwające'}
                  >
                    {t('home.ongoingShort') || 'Trwające'}
                  </SegmentButton>
                </SegmentedControl>
              </ToggleWrapper>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FilterButtons>
                <FilterButton 
                  active={period === 'day'} 
                  onClick={() => setPeriod('day')}
                >
                  {t('home.period.today')}
                </FilterButton>
                <FilterButton 
                  active={period === 'week'} 
                  onClick={() => setPeriod('week')}
                >
                  {t('home.period.week')}
                </FilterButton>
                <FilterButton 
                  active={period === 'month'} 
                  onClick={() => setPeriod('month')}
                >
                  {t('home.period.month')}
                </FilterButton>
              </FilterButtons>
            </div>
          </CardHeader>

          {isLoading ? (
            <EmptyState>
              <p>{t('home.loadingReservations')}</p>
            </EmptyState>
          ) : filteredRentals.length > 0 ? (
            <>
              {currentRentals.map(rental => (
                <RentalListItem key={rental.id} onClick={() => openReservationInCalendar(rental)}>
                  <ItemColumn>
                    <ItemLabel>Sprzęt</ItemLabel>
                    <ItemValue>{rental.equipment}</ItemValue>
                  </ItemColumn>
                  <ItemColumn>
                    <ItemLabel>Data rozpoczęcia</ItemLabel>
                    <ItemValue>{rental.startDate}</ItemValue>
                  </ItemColumn>
                  <ItemColumn>
                    <ItemLabel>Długość</ItemLabel>
                    <Badge color="var(--primary)">
                      {rental.days} dni
                    </Badge>
                  </ItemColumn>
                  <ItemColumn>
                    <ItemLabel>Klient</ItemLabel>
                    <ItemValue>{rental.name}</ItemValue>
                    {rental.phone && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {formatPhone(rental.phone)}
                      </span>
                    )}
                  </ItemColumn>
                  <Dziubek aria-hidden="true" title="Otwórz w kalendarzu">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 4 L19 12 L5 20 Z" fill="currentColor"/>
                    </svg>
                  </Dziubek>
                </RentalListItem>
              ))}

              {totalPages > 1 && (
                <PaginationControls>
                  <PaginationButton 
                    onClick={handlePrevPage} 
                    disabled={currentPage === 1}
                  >
                    ← {t('home.pagination.prev')}
                  </PaginationButton>
                  <PageInfo>
                    {t('home.pagination.page')} {currentPage} {t('home.pagination.of')} {totalPages}
                  </PageInfo>
                  <PaginationButton 
                    onClick={handleNextPage} 
                    disabled={currentPage === totalPages}
                  >
                    {t('home.pagination.next')} →
                  </PaginationButton>
                </PaginationControls>
              )}
            </>
          ) : (
            <EmptyState>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h4>{t('home.noRentals')}</h4>
              <p>
                {showOngoing
                  ? 'Brak trwających rezerwacji'
                  : 'Brak zaplanowanych rezerwacji w tym okresie'}
              </p>
            </EmptyState>
          )}
        </Card>

        <Card>
          <CardHeader>
            <SectionTitle style={{ margin: 0 }}>{t('home.bestSellers.title')}</SectionTitle>
          </CardHeader>

          {/* ZAMIEŃ CategoryTabs na: */}
          <CategoryDropdown ref={categoryDropdownRef}>
            <CategoryButton
              data-open={categoryDropdownOpen}
              onClick={() => categories.length && setCategoryDropdownOpen(prev => !prev)}
              disabled={!categories.length}
            >
              <span>
                {selectedCategoryLabel === 'all' 
                  ? (t('home.category.all') || 'Wszystkie kategorie')
                  : selectedCategoryLabel
                }
              </span>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none"
                style={{ 
                  transition: 'transform 0.2s ease',
                  transform: categoryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                <path 
                  d="M6 9l6 6 6-6" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </CategoryButton>

            {categoryDropdownOpen && categories.length > 0 && (
              <CategoryList>
                {categories.map(cat => (
                  <CategoryItem
                    key={cat.key}
                    data-selected={category === cat.value}
                    onClick={() => handleCategoryChange(cat.value)}
                  >
                    {cat.label === 'all' 
                      ? (t('home.category.all') || 'Wszystkie kategorie')
                      : cat.label
                    }
                  </CategoryItem>
                ))}
              </CategoryList>
            )}
          </CategoryDropdown>

          <TopEquipmentGrid>
            {isLoadingBest ? (
              <EmptyState>
                <p>{t('home.loading')}</p>
              </EmptyState>
            ) : bestSellers.length > 0 ? (
              bestSellers.map((item, idx) => (
                <TopEquipmentItem key={item.id || idx}>
                  <RankBadge rank={idx + 1}>
                    {idx + 1}
                  </RankBadge>
                  <EquipmentInfo>
                    <EquipmentName>{item.name}</EquipmentName>
                    <EquipmentMeta>
                      {item.brand} • {item.size} • {item.category}
                    </EquipmentMeta>
                  </EquipmentInfo>
                  <RentalCount>
                    {item.rentalCount ?? item.rental_count ?? 0}
                  </RentalCount>
                </TopEquipmentItem>
              ))
            ) : (
              <EmptyState>
                <h4>{t('home.noData')}</h4>
                <p>Brak danych o wypożyczeniach</p>
              </EmptyState>
            )}
          </TopEquipmentGrid>
        </Card>
      </ContentGrid>
    </Container>
  );
}